import React, { useEffect, useRef, useState } from "react";
import api from "../api";
import {
  Database, HardDrive, RefreshCw, Trash2, Play, AlertTriangle,
  CheckCircle2, XCircle, Loader, Wifi, Download,
} from "lucide-react";

const fmtBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024*1024) return `${(n/1024).toFixed(1)} KB`;
  if (n < 1024*1024*1024) return `${(n/1024/1024).toFixed(1)} MB`;
  return `${(n/1024/1024/1024).toFixed(2)} GB`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { dateStyle:"short", timeStyle:"short" }); }
  catch { return iso; }
};

export default function BackupRestore() {
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [ftpDir, setFtpDir] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ftpStatus, setFtpStatus] = useState(null);
  const [restoreFor, setRestoreFor] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  const reload = async ({ silent = false } = {}) => {
    if (!silent) setError("");
    try {
      const [a, b] = await Promise.all([
        api.get("/backup/list"),
        api.get("/backup/jobs?limit=10"),
      ]);
      setItems(a.data.items || []);
      setFtpDir(a.data.ftpDir || "");
      setJobs(b.data.items || []);
      // Reset error after a successful refresh (recover from previous transient)
      if (silent) setError("");
    } catch (e) {
      // During polling we silently swallow transient errors (502/timeout while
      // a large backup is uploading is normal on K8s ingresses). Only the first
      // manual load or an explicit user action surfaces the error banner.
      if (!silent) {
        setError(e.response?.data?.detail || "Errore caricamento");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  // Auto-poll while a job is queued/running. We use silent mode so transient
  // 502/timeout errors (very common while a large backup is uploading via
  // the K8s ingress) don't surface as scary red banners — only persistent
  // failures of the user-triggered initial load do.
  useEffect(() => {
    const active = jobs.some(j => j.status === "running" || j.status === "queued");
    if (active && !pollRef.current) {
      pollRef.current = setInterval(() => reload({ silent: true }), 4000);
    }
    if (!active && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current && !active) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [jobs]);

  const checkFtp = async () => {
    setFtpStatus({ loading: true });
    try {
      const { data } = await api.get("/backup/ftp-check");
      setFtpStatus(data);
    } catch (e) {
      setFtpStatus({ ok:false, error: e.message });
    }
  };

  const runBackup = async (mode) => {
    if (!window.confirm(`Avviare un backup ${mode==="full" ? "completo (app + database)" : "del solo database"}?\nL'upload su FTP può richiedere alcuni minuti.`)) return;
    setBusy(true); setError("");
    try {
      await api.post("/backup/run", { mode });
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || "Errore avvio backup");
    } finally { setBusy(false); }
  };

  const deleteItem = async (filename) => {
    if (!window.confirm(`Eliminare definitivamente "${filename}" dal server FTP?\nQuesta operazione non è reversibile.`)) return;
    setBusy(true); setError("");
    try {
      await api.delete(`/backup/${encodeURIComponent(filename)}`);
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || "Errore eliminazione");
    } finally { setBusy(false); }
  };

  const restore = async () => {
    if (!restoreFor) return;
    if (confirmText.trim().toUpperCase() !== "RIPRISTINA") {
      setError('Devi digitare esattamente "RIPRISTINA" per confermare.');
      return;
    }
    setBusy(true); setError("");
    try {
      await api.post(`/backup/restore/${encodeURIComponent(restoreFor.filename)}`,
        { confirm: confirmText });
      setRestoreFor(null); setConfirmText("");
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || "Errore ripristino");
    } finally { setBusy(false); }
  };

  const activeJob = jobs.find(j => j.status === "queued" || j.status === "running");

  const statusBadge = (s) => {
    const styles = {
      queued:  { color:"#0369a1", bg:"#dbeafe", label:"In coda", icon: <Loader size={11} className="animate-spin"/> },
      running: { color:"#0369a1", bg:"#dbeafe", label:"In esecuzione", icon: <Loader size={11} className="animate-spin"/> },
      success: { color:"#166534", bg:"#dcfce7", label:"Completato", icon: <CheckCircle2 size={11}/> },
      failed:  { color:"#7f1d1d", bg:"#fee2e2", label:"Fallito", icon: <XCircle size={11}/> },
    }[s] || { color:"#475569", bg:"#f1f5f9", label:s };
    return (
      <span style={{display:"inline-flex", alignItems:"center", gap:4,
        padding:"2px 7px", borderRadius:10, fontSize:11, fontWeight:700,
        color: styles.color, background: styles.bg}}>
        {styles.icon}{styles.label}
      </span>
    );
  };

  return (
    <div className="card mb-4" data-testid="backup-card">
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="h-section" style={{display:"flex", alignItems:"center", gap:8}}>
            <HardDrive size={18}/> Backup & Restore
          </div>
          <div className="text-small text-muted" style={{marginTop:4, lineHeight:1.55, maxWidth:680}}>
            Backup completo del database (BSON compresso) e opzionale tarball dell'intera
            applicazione, caricato sul tuo server FTP <code>{ftpDir || "—"}</code>.
            I file non vengono mai eliminati automaticamente: gestiscili da qui.
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={checkFtp} data-testid="ftp-check-btn">
          <Wifi size={14}/> Test FTP
        </button>
      </div>

      {ftpStatus && !ftpStatus.loading && (
        <div className="card mt-2" style={{
          padding:10,
          background: ftpStatus.ok ? "#dcfce7" : "#fef2f2",
          borderLeft: `3px solid ${ftpStatus.ok ? "#15803d" : "#b91c1c"}`,
        }}>
          <div className="text-small" style={{color: ftpStatus.ok ? "#166534" : "#7f1d1d"}}>
            {ftpStatus.ok
              ? <>✓ Connesso a <strong>{ftpStatus.host}</strong> (utente <strong>{ftpStatus.user}</strong>, dir <code>{ftpStatus.pwd}</code>)</>
              : <>✗ {ftpStatus.error}</>}
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-3 mb-3" style={{flexWrap:"wrap"}}>
        <button className="btn btn-primary" disabled={busy || !!activeJob}
          onClick={()=>runBackup("db")} data-testid="backup-db-btn">
          <Database size={14}/> Backup solo database
        </button>
        <button className="btn btn-secondary" disabled={busy || !!activeJob}
          onClick={()=>runBackup("full")} data-testid="backup-full-btn">
          <HardDrive size={14}/> Backup completo (app + DB)
        </button>
        <button className="btn btn-secondary btn-sm" onClick={reload} disabled={loading}>
          <RefreshCw size={14}/> Aggiorna lista
        </button>
      </div>

      {activeJob && (
        <div className="card mb-3" style={{padding:10, background:"#dbeafe", borderLeft:"3px solid #0369a1"}}>
          <div className="text-small" style={{color:"#0c4a6e", display:"flex", alignItems:"center", gap:8}}>
            <Loader size={14} className="animate-spin"/>
            <strong>{activeJob.kind === "backup" ? "Backup" : "Ripristino"} in corso</strong>
            ({activeJob.mode}) ·
            {activeJob.steps?.[activeJob.steps.length-1]?.msg || "avviato"}
          </div>
        </div>
      )}

      {error && (
        <div className="card mb-3" style={{padding:10, background:"#fef2f2", borderLeft:"3px solid #b91c1c",
             display:"flex", alignItems:"center", gap:8}}>
          <AlertTriangle size={14} style={{color:"#b91c1c", flexShrink:0}}/>
          <div className="text-small" style={{color:"#7f1d1d", flex:1}}>{error}</div>
          <button onClick={()=>setError("")} className="btn-icon" title="Chiudi"
            style={{padding:2, color:"#7f1d1d"}} data-testid="dismiss-error">
            ×
          </button>
        </div>
      )}

      {/* ---- Backup list ---- */}
      <div className="text-small" style={{fontWeight:700, marginBottom:6}}>
        Backup disponibili su FTP ({items.length})
      </div>
      {loading ? <span className="spinner"/> :
       items.length === 0 ? (
        <div className="text-small text-muted" style={{padding:16, background:"var(--mv-bg-soft)", borderRadius:6, textAlign:"center"}}>
          Nessun backup presente. Avvia il primo qui sopra.
        </div>
      ) : (
        <table className="tbl" data-testid="backup-list">
          <thead><tr>
            <th>File</th><th>Tipo</th><th>Dimensione</th><th>Data</th><th style={{textAlign:"right"}}>Azioni</th>
          </tr></thead>
          <tbody>
            {items.map(it => (
              <tr key={it.filename}>
                <td><code style={{fontSize:11}}>{it.filename}</code></td>
                <td>
                  {it.kind === "full"
                    ? <span className="badge badge-orange">App + DB</span>
                    : <span className="badge badge-blue">Solo DB</span>}
                </td>
                <td>{fmtBytes(it.size)}</td>
                <td className="text-small">{fmtDate(it.mtime)}</td>
                <td style={{textAlign:"right", whiteSpace:"nowrap"}}>
                  <button className="btn-icon" title="Ripristina questo backup"
                    onClick={()=>{setRestoreFor(it); setConfirmText(""); setError("");}}
                    disabled={busy || !!activeJob} data-testid={`restore-btn-${it.filename}`}>
                    <Download size={14}/>
                  </button>
                  <button className="btn-icon" title="Elimina dal server FTP"
                    onClick={()=>deleteItem(it.filename)} disabled={busy || !!activeJob}>
                    <Trash2 size={14}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ---- Job log ---- */}
      {jobs.length > 0 && (
        <details className="mt-4" style={{cursor:"pointer"}}>
          <summary className="text-small" style={{fontWeight:700}}>Cronologia operazioni ({jobs.length})</summary>
          <table className="tbl mt-2" style={{fontSize:12}}>
            <thead><tr>
              <th>Tipo</th><th>Modalità</th><th>Stato</th><th>File</th><th>Quando</th>
            </tr></thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id}>
                  <td>{j.kind === "backup" ? "↑ Backup" : "↓ Restore"}</td>
                  <td>{j.mode}</td>
                  <td>{statusBadge(j.status)}{j.errorMessage && <div className="text-small" style={{color:"#b91c1c", marginTop:3}}>{j.errorMessage}</div>}</td>
                  <td><code style={{fontSize:10}}>{j.filename || "—"}</code></td>
                  <td className="text-small text-muted">{fmtDate(j.completedAt || j.startedAt || j.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {/* ---- Restore confirmation modal ---- */}
      {restoreFor && (
        <div className="modal-backdrop" onClick={()=>!busy && setRestoreFor(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()} data-testid="restore-modal" style={{maxWidth:560}}>
            <div className="flex gap-3" style={{alignItems:"flex-start"}}>
              <div style={{
                width:42, height:42, borderRadius:"50%",
                background:"#fef2f2", color:"#b91c1c",
                display:"flex", alignItems:"center", justifyContent:"center",
                flexShrink:0,
              }}>
                <AlertTriangle size={22}/>
              </div>
              <div style={{flex:1}}>
                <div className="h-section" style={{margin:0, color:"#7f1d1d"}}>Ripristino irreversibile</div>
                <div className="text-small text-muted" style={{marginTop:4, lineHeight:1.55}}>
                  Stai per ripristinare l'intero database dal file:
                </div>
                <div style={{padding:8, background:"var(--mv-bg-soft)", borderRadius:6, margin:"8px 0"}}>
                  <code style={{fontSize:12}}>{restoreFor.filename}</code>
                  <div className="text-small text-muted" style={{marginTop:3}}>
                    {fmtBytes(restoreFor.size)} · {fmtDate(restoreFor.mtime)}
                  </div>
                </div>
                <div className="text-small" style={{color:"#7f1d1d", lineHeight:1.55}}>
                  <strong>Tutti i dati attuali verranno sostituiti</strong> con quelli del backup
                  (collection per collection, drop &amp; insert). I file dell'applicazione
                  non verranno toccati. L'operazione richiede 1-3 minuti.
                </div>

                <label className="label mt-3">
                  Per confermare, digita <strong>RIPRISTINA</strong>:
                </label>
                <input className="input" value={confirmText}
                  onChange={e=>setConfirmText(e.target.value)}
                  placeholder="RIPRISTINA"
                  autoFocus
                  data-testid="restore-confirm"/>

                {error && (
                  <div className="text-small" style={{color:"#b91c1c", marginTop:6}}>{error}</div>
                )}

                <div className="flex gap-2 justify-end mt-4">
                  <button className="btn btn-secondary" onClick={()=>setRestoreFor(null)} disabled={busy}>
                    Annulla
                  </button>
                  <button className="btn btn-primary"
                    style={{background:"#b91c1c", borderColor:"#b91c1c"}}
                    onClick={restore} disabled={busy || confirmText.trim().toUpperCase() !== "RIPRISTINA"}
                    data-testid="restore-submit">
                    {busy ? <span className="spinner"/> : <><Download size={14}/> Esegui ripristino</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
